/* eslint-disable sonarjs/cognitive-complexity */

'use client';

import { motion } from 'framer-motion';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useInViewScroll, StackedContent } from 'ui';
import { useWindowSize } from '@uidotdev/usehooks';
import { Sweepstake } from '../Sweepstake';

type SweepstakeItem = {
  id: string;
  headingBold?: string;
  heading?: string;
  body?: string;
  successTitle?: string;
  successMessage?: string;
  errorMessage?: string;
  ctaLabel?: string;
  startDate: string;
  endDate: string;
  rulesLink?: string;
  hideWhenSweepstakeEnded?: boolean;
  tabTitle?: string;
  tabLabel?: string;
};

type SweepstakesGalleryProps = {
  stackedContent?: any[];
  sweepstakes: SweepstakeItem[];
  useSweepstakeBackground?: boolean;
};

const shouldShowSweepstake = (item: SweepstakeItem) => {
  if (!item.hideWhenSweepstakeEnded) {
    return true;
  }

  const now = new Date();
  const startDate = new Date(item.startDate);
  const endDate = new Date(item.endDate);

  return now >= startDate && now <= endDate;
};

const getTabLabel = (item: SweepstakeItem) => {
  if (item.tabTitle) {
    return item.tabTitle;
  }

  if (item.tabLabel) {
    return item.tabLabel;
  }

  if (item.headingBold) {
    return item.headingBold
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  return item.id;
};

const SweepstakesGalleryItem = ({ sweepstake, hideGradient, isMobile }: { sweepstake: SweepstakeItem; hideGradient: boolean; isMobile: boolean }) => {
  return (
    <div className={isMobile ? 'w-full max-w-none shrink-0 snap-center' : 'w-[85vw] max-w-[1200px] shrink-0 snap-center'}>
      <Sweepstake
        id={sweepstake.id}
        headingBold={sweepstake.headingBold}
        heading={sweepstake.heading}
        body={sweepstake.body}
        successTitle={sweepstake.successTitle}
        successMessage={sweepstake.successMessage}
        errorMessage={sweepstake.errorMessage}
        ctaLabel={sweepstake.ctaLabel}
        startDate={sweepstake.startDate}
        endDate={sweepstake.endDate}
        rulesLink={sweepstake.rulesLink}
        hideWhenSweepstakeEnded={sweepstake.hideWhenSweepstakeEnded}
        hideGradient={hideGradient}
      />
    </div>
  );
};

const SweepstakesGallery = ({ stackedContent = [], sweepstakes, useSweepstakeBackground = false }: SweepstakesGalleryProps) => {
  const { ref } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleSweepstakes = useMemo(() => sweepstakes.filter(shouldShowSweepstake), [sweepstakes]);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const [offsets, setOffsets] = useState<number[]>([]);

  const { width: windowWidth } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 1100);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = containerRef.current;
      const card = cardRefs.current[index];
      if (!el || !card || !isMobile) return;

      setIsUserScrolling(true);

      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      const containerWidth = el.clientWidth;
      const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

      el.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth',
      });

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 600);
    },
    [isMobile],
  );

  const handleSelect = (index: number) => {
    setCurrentIndex(index);
    if (isMobile) {
      scrollToIndex(index);
    }
  };

  useEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const el = containerRef.current;
    let rafId = 0;

    const updateCurrentIndex = () => {
      if (isUserScrolling) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      cardRefs.current.forEach((card, index) => {
        if (!card) return;

        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distance = Math.abs(scrollCenter - cardCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== currentIndex) {
        setCurrentIndex(closestIndex);
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isMobile, currentIndex, isUserScrolling]);

  useEffect(() => {
    if (isMobile || !containerRef.current || !cardRefs.current.length) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const newOffsets = cardRefs.current.map(card => {
      if (!card) return 0;
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      return containerCenter - cardCenter;
    });

    setOffsets(newOffsets);
  }, [visibleSweepstakes.length, windowWidth, isMobile]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentIndex > visibleSweepstakes.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, visibleSweepstakes.length]);

  const sectionBackgroundClass = useSweepstakeBackground ? 'bg-gradient-to-b from-[#5B2E4F] to-[#150E14]' : 'bg-white';
  const headingColorClass = useSweepstakeBackground ? 'text-white' : 'text-xumoDarkPlum';
  const activeTabTextClass = useSweepstakeBackground ? 'text-xumoDarkPlum bg-white' : 'text-xumoDarkPlum bg-white';
  const inactiveTabTextClass = useSweepstakeBackground ? 'text-white/70 bg-white/10' : 'text-xumoDarkPlum/50 bg-white';
  const activeDotClass = useSweepstakeBackground ? 'bg-white w-8' : 'bg-xumoDarkPlum w-8';
  const inactiveDotClass = useSweepstakeBackground ? 'bg-white/30' : 'bg-xumoDarkPlum/30';
  const hideCardGradient = useSweepstakeBackground;

  if (!visibleSweepstakes.length) {
    return (
      <motion.section
        ref={ref}
        className={`relative z-40 flex w-full flex-col justify-center py-0 ${sectionBackgroundClass}`}
        data-testid={'sweepstakes-gallery-section-empty'}
      >
        <div className={`wrapper pt-20 ${headingColorClass}`}>
          <StackedContent
            full={false}
            fullalways
            fullheight
            mobileCenter={false}
            mobileCenterLg
            center
            light={false}
            noOrphans={false}
            space
            minHeight={false}
            left={false}
            noMargin={false}
            margin="0px 0px -20% 0px"
            content={stackedContent}
            className={headingColorClass}
          />
        </div>

        <div className="my-4 flex items-center justify-center md:my-8">
          <SweepstakesGalleryItem
            isMobile={isMobile}
            hideGradient={hideCardGradient}
            sweepstake={{
              id: 'no-active-sweepstakes',
              startDate: '2999-01-01T00:00:00Z',
              endDate: '2999-01-02T00:00:00Z',
              hideWhenSweepstakeEnded: false,
            }}
          />
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      ref={ref}
      className={`relative z-40 flex w-full flex-col justify-center py-0 ${sectionBackgroundClass}`}
      data-testid={'sweepstakes-gallery-section'}
    >
      <div className={`wrapper pt-20 ${headingColorClass}`}>
        <StackedContent
          full={false}
          fullalways
          fullheight
          mobileCenter={false}
          mobileCenterLg
          center
          light={false}
          noOrphans={false}
          space
          minHeight={false}
          left={false}
          noMargin={false}
          margin="0px 0px -20% 0px"
          content={stackedContent}
          className={headingColorClass}
        />
      </div>

      {visibleSweepstakes.length > 1 && (
        <div className="mx-auto mb-6 mt-8 flex justify-center px-4 md:mt-16 xl:mb-8">
          <div className="flex flex-col items-center gap-3 md:flex-row md:gap-4 xl:gap-8">
            {visibleSweepstakes.map((item, index) => (
              <div
                key={'sweepstakesGalleryButton' + item.id}
                className={`rounded-full ${currentIndex === index ? 'to-xumoDarkPlum bg-gradient-to-b from-[#945B87] via-[#66345B] p-[2px]' : 'bg-white'}`}
              >
                <button
                  onClick={() => handleSelect(index)}
                  className={`weight-500 rounded-full px-4 py-1 text-base tracking-[-0.2px] md:px-6 md:text-lg md:tracking-[-0.5px] ${
                    currentIndex === index ? activeTabTextClass : inactiveTabTextClass
                  }`}
                >
                  {getTabLabel(item)}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="my-4 flex items-center justify-center md:my-8">
        <motion.div
          ref={containerRef}
          className={[
            'flex w-full items-center',
            isMobile ? 'scrollbar-hide touch-auto snap-x snap-mandatory overflow-x-auto overscroll-x-contain' : 'gap-8',
          ].join(' ')}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          animate={isMobile ? {} : { x: offsets[currentIndex] || 0 }}
          transition={{
            type: 'spring',
            stiffness: 250,
            damping: 50,
            duration: 0.6,
          }}
        >
          {visibleSweepstakes.map((item, index) => (
            <div
              key={item.id ?? index}
              ref={el => {
                cardRefs.current[index] = el;
              }}
              className={['transition-all duration-300', isMobile ? 'w-full shrink-0' : 'mr-8 last:mr-0'].join(' ')}
              style={
                isMobile
                  ? {
                      scrollSnapAlign: 'center',
                      scrollSnapStop: 'always',
                    }
                  : {}
              }
            >
              <SweepstakesGalleryItem sweepstake={item} hideGradient={hideCardGradient} isMobile={isMobile} />
            </div>
          ))}
        </motion.div>
      </div>

      {visibleSweepstakes.length > 1 && (
        <div className="mb-4 mt-4 flex justify-center gap-2 lg:mt-0">
          {visibleSweepstakes.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? activeDotClass : inactiveDotClass}`}
              aria-label={`Go to sweepstake ${index + 1}`}
            />
          ))}
        </div>
      )}
    </motion.section>
  );
};

export default SweepstakesGallery;
